import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  FileText, Check, X, Building, MapPin, Calendar, 
  DollarSign, Clock, CheckCircle, XCircle, AlertCircle, Loader2
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const EstimatePortalPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [approving, setApproving] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [actionComplete, setActionComplete] = useState(null); // 'approved' or 'declined'

  useEffect(() => {
    fetchEstimate();
  }, [token]);

  const fetchEstimate = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/portal/estimate/${token}`);
      setData(response.data);
      
      // Check if already approved/declined
      if (response.data.estimate.status === 'approved') {
        setActionComplete('approved');
      } else if (response.data.estimate.status === 'declined') {
        setActionComplete('declined');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to load estimate. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setApproving(true);
      await axios.post(`${API}/api/portal/estimate/${token}/approve?customer_name=${encodeURIComponent(customerName || 'Customer')}`);
      setActionComplete('approved');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to approve estimate');
    } finally {
      setApproving(false);
    }
  };

  const handleDecline = async () => {
    try {
      setDeclining(true);
      await axios.post(`${API}/api/portal/estimate/${token}/decline?reason=${encodeURIComponent(declineReason)}`);
      setActionComplete('declined');
      setShowDeclineDialog(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to decline estimate');
    } finally {
      setDeclining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading estimate...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Estimate</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Link to="/">
              <Button variant="outline">Return to Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { estimate, customer, property, company } = data;

  // Show success/declined message
  if (actionComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            {actionComplete === 'approved' ? (
              <>
                <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Estimate Approved!</h2>
                <p className="text-gray-600 mb-6">
                  Thank you for approving this estimate. {company.name} will be in touch shortly to schedule your service.
                </p>
              </>
            ) : (
              <>
                <XCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Estimate Declined</h2>
                <p className="text-gray-600 mb-6">
                  You've declined this estimate. If you have questions or would like a revised quote, please contact {company.name}.
                </p>
              </>
            )}
            {company.email && (
              <a href={`mailto:${company.email}`}>
                <Button variant="outline">Contact {company.name}</Button>
              </a>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{company.name}</h1>
              <p className="text-sm text-gray-500">Service Estimate</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Estimate #{estimate.id.slice(0, 8).toUpperCase()}</p>
              <p className="text-sm text-gray-500">
                {new Date(estimate.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Estimate Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Property Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  Service Location
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-medium text-gray-900">{property?.address}</p>
                {property?.city && (
                  <p className="text-gray-600">{property.city}, {property.state} {property.zip_code}</p>
                )}
                {property?.property_size && (
                  <p className="text-sm text-gray-500 mt-2">
                    Property Size: {property.property_size.toLocaleString()} {property.size_unit}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Service Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {estimate.line_items.map((item, index) => (
                    <div key={index} className="flex justify-between items-start pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{item.description}</p>
                        <p className="text-sm text-gray-500">
                          {item.quantity} × ${item.unit_price.toFixed(2)}
                        </p>
                      </div>
                      <p className="font-semibold text-gray-900">${item.total.toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                {/* Total */}
                <div className="border-t border-gray-200 mt-6 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900">Total</span>
                    <span className="text-2xl font-bold text-primary">${estimate.total.toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            {estimate.notes && (
              <Card>
                <CardHeader>
                  <CardTitle>Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 whitespace-pre-wrap">{estimate.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Action Panel */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle>Your Response</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {estimate.valid_until && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 bg-amber-50 p-3 rounded-lg">
                    <Clock className="h-4 w-4 text-amber-600" />
                    <span>Valid until {new Date(estimate.valid_until).toLocaleDateString()}</span>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name (Optional)
                  </label>
                  <Input
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder={customer?.name || "Your name"}
                    data-testid="customer-name-input"
                  />
                </div>

                <Button
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  size="lg"
                  onClick={handleApprove}
                  disabled={approving}
                  data-testid="approve-estimate-btn"
                >
                  {approving ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <Check className="h-5 w-5 mr-2" />
                  )}
                  Approve Estimate
                </Button>

                <Button
                  className="w-full"
                  variant="outline"
                  size="lg"
                  onClick={() => setShowDeclineDialog(true)}
                  data-testid="decline-estimate-btn"
                >
                  <X className="h-5 w-5 mr-2" />
                  Decline
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  Questions? Contact us at{' '}
                  {company.email ? (
                    <a href={`mailto:${company.email}`} className="text-primary hover:underline">
                      {company.email}
                    </a>
                  ) : (
                    company.name
                  )}
                  {company.phone && ` or ${company.phone}`}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Decline Dialog */}
      <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline Estimate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-gray-600">
              Please let us know why you're declining this estimate so we can better serve you in the future.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason (Optional)
              </label>
              <Textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Too expensive, timing doesn't work, found another provider, etc."
                rows={3}
                data-testid="decline-reason-input"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowDeclineDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDecline}
                disabled={declining}
                data-testid="confirm-decline-btn"
              >
                {declining ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Decline Estimate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EstimatePortalPage;
